import { useEffect, useRef, useState } from "react";
import EntityErrorPage from "../../common-components/NotFoundPage";
import ImageWithLoader from "../../common-components/ImageWithLoader";
import Avatar from "../../common-components/Avatar";
import ReportDialog from "../../common-components/ReportDialog";
import UnfollowConfirmDialog from "../../common-components/UnfollowConfirmDialog";
import VerifiedBadge from "../../common-components/VerifiedBadge";
import MessageShopButton from "../../common-components/MessageShopButton";
import CategoryChip from "../../common-components/CategoryChip";
import ProductCard from "../../common-components/Product/ProductCard";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import useAuthentication from "../../hooks/useAuthentication";
import useGoToLogin from "../../hooks/useGoToLogin";
import { useFollowShop } from "../../hooks/useFollowActions";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LanguageIcon from "@mui/icons-material/Language";
import InstagramIcon from "@mui/icons-material/Instagram";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { parsePhoneNumber } from "react-phone-number-input";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getPublicShop,
  getShopProducts,
  getShopByHandle,
} from "../../utility/httpActions/shopActions";
import { resolveImageUri, shopCategories } from "../../utility/constants";
import { frontendRoutes } from "../../utility/frontendRoutes";
import { trackEvent } from "../../utility/analytics";
import {
  buildSearchFilterPath,
  buildShopPath,
  buildUserPath,
} from "../../utility/utilityFunctions";
import DeliveryInstructionsBox from "../../common-components/DeliveryInstructionsBox";
import ExternalLink from "../../common-components/ExternalLink";

const PAGE_SIZE = 20;

const DELIVERY_METHOD_LABELS = {
  PICKUP: "Pickup",
  DELIVERY: "Delivery",
  SERVICE: "Service",
};

const DELIVERY_METHOD_ICONS = {
  PICKUP: <StorefrontOutlinedIcon sx={{ fontSize: 16 }} />,
  DELIVERY: <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />,
  SERVICE: <WorkOutlineOutlinedIcon sx={{ fontSize: 16 }} />,
};

const PAYMENT_METHOD_LABELS = {
  CASH_ON_DELIVERY: "Cash on Delivery",
  BANK_TRANSFER: "Bank Transfer",
  VENMO: "Venmo",
  ZELLE: "Zelle",
};

const PAYMENT_METHOD_ICONS = {
  CASH_ON_DELIVERY: <MonetizationOnOutlinedIcon sx={{ fontSize: 16 }} />,
  BANK_TRANSFER: <AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />,
  VENMO: <MonetizationOnOutlinedIcon sx={{ fontSize: 16 }} />,
  ZELLE: <MonetizationOnOutlinedIcon sx={{ fontSize: 16 }} />,
};

const SORT_OPTIONS = [
  { label: "Newest", value: "newest" },
  { label: "Price ↑", value: "price_asc" },
  { label: "Price ↓", value: "price_desc" },
  { label: "Top Rated", value: "top_rated" },
  { label: "Most Sold", value: "most_sold" },
];

const formatLabel = (val) =>
  (val || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getCategoryLabel = (value) => {
  const cat = shopCategories.find((c) => c.value === value);
  return cat ? `${cat.icon} ${cat.label}` : formatLabel(value);
};

const formatJoinedDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const formatPhone = (phone) => {
  if (!phone) return null;
  try {
    return parsePhoneNumber(phone)?.formatNational() || phone;
  } catch {
    return phone;
  }
};

const ContactRow = ({ icon: Icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
    <Icon sx={{ fontSize: 15, color: "text.disabled" }} />
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

const StatItem = ({ value, label }) => (
  <Box sx={{ textAlign: "center", mr: 3 }}>
    <Typography variant="h6" fontWeight={700} color="#046287" lineHeight={1.2}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ShopPage = () => {
  const { shopId: routeShopId } = useParams();
  const navigate = useNavigate();
  const goToLogin = useGoToLogin();
  // Handle-based shop links (e.g. "/shop/@chiso-naturals") share the :shopId param —
  // detect the "@" prefix to resolve by handle instead of by id.
  const isHandleLink = routeShopId?.startsWith("@");
  const handle = isHandleLink ? routeShopId.slice(1) : undefined;
  const shopId = isHandleLink ? undefined : routeShopId;
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState("newest");
  const [reportOpen, setReportOpen] = useState(false);
  const [unfollowConfirmOpen, setUnfollowConfirmOpen] = useState(false);
  const [shareSnackbar, setShareSnackbar] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const debouncedSearch = useDebounced(searchInput, 400);
  const sentinelRef = useRef(null);
  const { isLoggedIn } = useAuthentication();

  const {
    data: shop,
    isLoading: shopLoading,
    isError: shopError,
    error: shopErrorObj,
  } = useQuery({
    // isLoggedIn is in the key because the backend redacts contact info for
    // logged-out viewers; without it, returning from login briefly shows the
    // cached redacted copy.
    queryKey: handle
      ? ["publicShopByHandle", handle, isLoggedIn]
      : ["publicShop", shopId, isLoggedIn],
    queryFn: () => (handle ? getShopByHandle(handle) : getPublicShop(shopId)),
    enabled: !!(handle || shopId),
  });

  const resolvedShopId = shop?.id || shopId;
  // Must be called unconditionally before any early return
  const { mutate: toggleFollow, isPending: followPending } =
    useFollowShop(resolvedShopId);

  const {
    data: productsData,
    isLoading: productsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["shopPageProducts", resolvedShopId, sort, debouncedSearch],
    queryFn: ({ pageParam = 1 }) =>
      getShopProducts(resolvedShopId, {
        page: pageParam,
        limit: PAGE_SIZE,
        status: "PUBLISHED",
        sort,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: !!resolvedShopId,
  });

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (shopLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (shopError || !shop) {
    return (
      <EntityErrorPage
        errorStatus={shopErrorObj?.status ?? 404}
        variant="shop"
      />
    );
  }

  const coverUri = shop.coverPhoto ? resolveImageUri(shop.coverPhoto) : null;
  const profileUri = shop.profilePhoto
    ? resolveImageUri(shop.profilePhoto)
    : null;
  const followerCount =
    typeof shop.followersCount === "number"
      ? shop.followersCount
      : (shop.followers?.length || 0) + (shop.followerShops?.length || 0);
  const totalProducts = productsData?.pages?.[0]?.totalProducts ?? 0;
  const products = productsData?.pages?.flatMap((p) => p.products) ?? [];

  const shopFollowListVisibility =
    shop.followListVisibility ?? "FOLLOWERS_ONLY";
  const followListIsVisible =
    shopFollowListVisibility === "PUBLIC" ||
    (shopFollowListVisibility === "FOLLOWERS_ONLY" && shop.isFollowedByViewer);
  const followersHref = generatePath(frontendRoutes.SHOP_FOLLOWERS, {
    shopId: resolvedShopId,
  });
  const isFollowedByViewer = shop.isFollowedByViewer ?? false;
  const joinedDate = formatJoinedDate(shop.createdAt);

  // Mirrors the mobile storefront (CustomerShopInfo.js): phone and email only
  // show once the shop has verified them.
  const publicContactInfo = Array.isArray(shop.contactInfo)
    ? shop.contactInfo.filter((c) => c?.isPublic !== false && c?.value)
    : [];
  const phoneContact = publicContactInfo.find((c) => c.type === "PHONE");
  const emailContact = publicContactInfo.find((c) => c.type === "EMAIL");
  const websiteContact = publicContactInfo.find((c) => c.type === "WEBSITE");
  const socialContact = publicContactInfo.find((c) => c.type === "SOCIAL");
  const hasContacts =
    (phoneContact && shop.isPhoneVerified) ||
    (emailContact && shop.isEmailVerified) ||
    websiteContact ||
    socialContact ||
    shop.contactInfoRedacted;

  const handleShareShop = async () => {
    const shareLink = `${window.location.origin}${buildShopPath(shop)}`;
    const shareData = { title: `${shop.shopName} on Ngwaahia`, url: shareLink };
    trackEvent("shop_shared", {
      shopId: resolvedShopId,
      shopName: shop.shopName,
    });
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {}
    } else {
      await navigator.clipboard.writeText(shareLink);
      setShareSnackbar("Link copied to clipboard");
    }
  };

  // Note: this only updates the DOM after JS runs, so it fixes the browser
  // tab title and helps a JS-executing crawler (modern Googlebot) — it does
  // NOT produce link-preview cards on WhatsApp/Twitter/Facebook when a shop
  // URL is shared, since those scrapers don't execute JS. That needs
  // server-side meta injection in server.js (see dev-docs SEO plan).
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      <Helmet>
        <title>{shop.shopName} | Ngwaahia</title>
        <meta
          name="description"
          content={
            shop.description
              ? shop.description.slice(0, 160)
              : `${shop.shopName} on Ngwaahia — browse products and shop directly from this seller.`
          }
        />
      </Helmet>
      {/* Cover + avatar */}
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            width: "100%",
            height: { xs: 180, md: 260 },
            bgcolor: "#046287",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {coverUri ? (
            <ImageWithLoader src={coverUri} alt="" />
          ) : (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.3)",
                fontWeight: 700,
                fontSize: { xs: 64, md: 96 },
              }}
            >
              {(shop.shopName || "S")[0].toUpperCase()}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            position: "absolute",
            bottom: -40,
            left: { xs: 16, md: 32 },
            width: 80,
            height: 80,
            borderRadius: "50%",
            border: "3px solid white",
            overflow: "hidden",
            bgcolor: "#046287",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          {profileUri ? (
            <ImageWithLoader
              src={profileUri}
              alt={shop.shopName}
              sx={{ borderRadius: "50%" }}
            />
          ) : (
            <Typography sx={{ color: "white", fontWeight: 700, fontSize: 28 }}>
              {(shop.shopName || "S")[0].toUpperCase()}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Info section */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: 3, pt: 7 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            {shop.shopName}
            {shop.isIdentityVerified && <VerifiedBadge size={20} />}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button
              variant={isFollowedByViewer ? "outlined" : "contained"}
              size="small"
              disabled={followPending}
              onClick={() => {
                if (!isLoggedIn) {
                  goToLogin();
                  return;
                }
                if (isFollowedByViewer) {
                  setUnfollowConfirmOpen(true);
                } else {
                  toggleFollow(false);
                }
              }}
              sx={{
                borderRadius: 3,
                fontWeight: 600,
                minWidth: 96,
                ...(isFollowedByViewer
                  ? { borderColor: "#046287", color: "#046287" }
                  : { bgcolor: "#046287", "&:hover": { bgcolor: "#035070" } }),
              }}
            >
              {followPending
                ? "…"
                : isFollowedByViewer
                  ? "Following"
                  : "Follow"}
            </Button>
            <MessageShopButton
              shop={shop}
              source="shop_page"
              message={`Hello ${shop.shopName || "there"}, I found your shop on Ngwaahia and have a question.\n${window.location.origin}${buildShopPath(shop)}`}
            />
            <Tooltip title="Share shop">
              <IconButton
                size="small"
                onClick={handleShareShop}
                sx={{ color: "#046287" }}
              >
                <IosShareOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Report">
              <IconButton
                size="small"
                onClick={() => {
                  if (!isLoggedIn) {
                    goToLogin();
                    return;
                  }
                  setReportOpen(true);
                }}
                sx={{ color: "#9ca3af" }}
              >
                <FlagOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Categories */}
        {shop.categories?.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
            {shop.categories.map((c) => (
              <CategoryChip
                key={c}
                label={getCategoryLabel(c)}
                onClick={() => navigate(buildSearchFilterPath({ category: c }))}
              />
            ))}
          </Box>
        )}

        {/* Description */}
        {shop.description && (
          <Typography
            variant="body2"
            sx={{ mt: 1.5, color: "text.secondary", whiteSpace: "pre-wrap" }}
          >
            {shop.description}
          </Typography>
        )}

        {/* Order instructions */}
        {shop.orderInstructions && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Order Instructions
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}
            >
              {shop.orderInstructions}
            </Typography>
          </Box>
        )}

        {shop.deliveryMethods?.includes("DELIVERY") && (
          <DeliveryInstructionsBox
            instructions={shop.deliveryInstructions}
            title="Delivery instructions"
            sx={{ mt: 1.5 }}
          />
        )}

        {/* About row: location, joined date, owner */}
        {(shop.address?.city || joinedDate || shop.owner?.id) && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              rowGap: 0.5,
              columnGap: 2.5,
              mt: 2,
            }}
          >
            {shop.address?.city && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <LocationOnOutlinedIcon
                  sx={{ fontSize: 15, color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {[shop.address.city, shop.address.state, shop.address.country]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              </Box>
            )}
            {joinedDate && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarTodayOutlinedIcon
                  sx={{ fontSize: 14, color: "text.disabled" }}
                />
                <Typography variant="body2" color="text.secondary">
                  Joined {joinedDate}
                </Typography>
              </Box>
            )}
            {shop.showOwnerOnStorefront !== false && shop.owner?.id && (
              <Box
                component={Link}
                to={buildUserPath(shop.owner)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                }}
              >
                <Avatar
                  src={
                    shop.owner.profilePhoto?.path
                      ? resolveImageUri(shop.owner.profilePhoto)
                      : null
                  }
                  name={shop.owner.name}
                  size={16}
                />
                <Typography variant="body2" color="text.secondary">
                  by{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: "#046287", fontWeight: 600 }}
                  >
                    {shop.owner.name}
                  </Typography>
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* About this shop — only shown when owner is hidden from storefront */}
        {shop.showOwnerOnStorefront === false && shop.owner?.id && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="body2"
              onClick={() => setShowAbout((v) => !v)}
              sx={{
                color: "#046287",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                userSelect: "none",
              }}
            >
              About this shop
              {showAbout ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </Typography>
            {showAbout && (
              <Box
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f9f9f9",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {shop.shopName && (
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {shop.shopName}
                  </Typography>
                )}
                {shop.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {shop.description}
                  </Typography>
                )}
                {shop.orderInstructions && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      Order Instructions
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {shop.orderInstructions}
                    </Typography>
                  </Box>
                )}
                {shop.deliveryMethods?.includes("DELIVERY") && (
                  <DeliveryInstructionsBox
                    instructions={shop.deliveryInstructions}
                    title="Delivery instructions"
                  />
                )}
                {shop.categories?.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {shop.categories.map((c) => (
                      <CategoryChip
                        key={c}
                        label={getCategoryLabel(c)}
                        onClick={() =>
                          navigate(buildSearchFilterPath({ category: c }))
                        }
                      />
                    ))}
                  </Box>
                )}
                {shop.address?.city && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{ fontSize: 15, color: "text.disabled" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {[
                        shop.address.city,
                        shop.address.state,
                        shop.address.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </Box>
                )}
                {joinedDate && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <CalendarTodayOutlinedIcon
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Joined {joinedDate}
                    </Typography>
                  </Box>
                )}
                <Box
                  component={Link}
                  to={buildUserPath(shop.owner)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textDecoration: "none",
                  }}
                >
                  <Avatar
                    src={
                      shop.owner.profilePhoto?.path
                        ? resolveImageUri(shop.owner.profilePhoto)
                        : null
                    }
                    name={shop.owner.name}
                    size={28}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.2 }}
                    >
                      Owned by
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#046287", fontWeight: 600 }}
                    >
                      {shop.owner.name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Stats row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexWrap: "wrap",
            mt: 2.5,
            gap: 0.5,
          }}
        >
          <StatItem
            value={totalProducts}
            label={totalProducts === 1 ? "Product" : "Products"}
          />
          {shop.numReviews > 0 ? (
            <Box
              component={Link}
              to={generatePath(frontendRoutes.SHOP_REVIEWS, {
                shopId: resolvedShopId,
              })}
              sx={{ textDecoration: "none" }}
            >
              <StatItem
                value={shop.rating ? Number(shop.rating).toFixed(1) : "—"}
                label="Rating"
              />
            </Box>
          ) : (
            <StatItem value="—" label="Rating" />
          )}
          {shop.numReviews > 0 ? (
            <Box
              component={Link}
              to={generatePath(frontendRoutes.SHOP_REVIEWS, {
                shopId: resolvedShopId,
              })}
              sx={{ textDecoration: "none" }}
            >
              <StatItem
                value={shop.numReviews}
                label={shop.numReviews === 1 ? "Review" : "Reviews"}
              />
            </Box>
          ) : (
            <StatItem value={0} label="Reviews" />
          )}
          {followListIsVisible ? (
            <Box
              component={Link}
              to={followersHref}
              sx={{
                textDecoration: "none",
                "&:hover .stat-value": { color: "#046287" },
              }}
            >
              <StatItem
                value={followerCount}
                label={followerCount === 1 ? "Follower" : "Followers"}
              />
            </Box>
          ) : (
            <StatItem
              value={followerCount}
              label={followerCount === 1 ? "Follower" : "Followers"}
            />
          )}
        </Box>

        {/* Contact */}
        {hasContacts && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid #f3f4f6" }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.disabled",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                display: "block",
                mb: 1.25,
              }}
            >
              Contact
            </Typography>
            {shop.contactInfoRedacted ? (
              <Box
                component="button"
                type="button"
                onClick={goToLogin}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                  font: "inherit",
                }}
              >
                <LockOutlinedIcon
                  sx={{ fontSize: 15, color: "text.disabled" }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: "#046287", textDecoration: "underline" }}
                >
                  Log in to view contact details
                </Typography>
              </Box>
            ) : (
              <>
                {phoneContact?.value && shop.isPhoneVerified && (
                  <ContactRow icon={PhoneOutlinedIcon}>
                    {formatPhone(phoneContact.value)}
                    {phoneContact.isWhatsapp && (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        {" "}
                        (WhatsApp)
                      </Box>
                    )}
                  </ContactRow>
                )}
                {emailContact?.value && shop.isEmailVerified && (
                  <ContactRow icon={MailOutlineIcon}>
                    {emailContact.value}
                  </ContactRow>
                )}
                {websiteContact?.value && (
                  <ContactRow icon={LanguageIcon}>
                    <ExternalLink value={websiteContact.value} />
                  </ContactRow>
                )}
                {socialContact?.value && (
                  <ContactRow icon={InstagramIcon}>
                    <ExternalLink value={socialContact.value} />
                  </ContactRow>
                )}
              </>
            )}
          </Box>
        )}

        {/* Delivery + payment methods */}
        {(shop.deliveryMethods?.length > 0 ||
          shop.paymentMethods?.length > 0) && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid #f3f4f6" }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.disabled",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                display: "block",
                mb: 1.25,
              }}
            >
              How we work
            </Typography>
            {shop.deliveryMethods?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: shop.paymentMethods?.length > 0 ? 1.5 : 0,
                }}
              >
                {shop.deliveryMethods.map((method) => (
                  <Box
                    key={method}
                    onClick={() =>
                      navigate(
                        buildSearchFilterPath({
                          deliveryMethods: [method],
                          categories: shop.categories,
                        }),
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      color: "#046287",
                      cursor: "pointer",
                    }}
                  >
                    {DELIVERY_METHOD_ICONS[method]}
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#046287"
                    >
                      {DELIVERY_METHOD_LABELS[method] || formatLabel(method)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {shop.paymentMethods?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {shop.paymentMethods.map((method) => (
                  <Box
                    key={method}
                    onClick={() =>
                      navigate(
                        buildSearchFilterPath({
                          paymentMethods: [method],
                          categories: shop.categories,
                        }),
                      )
                    }
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      color: "#046287",
                      cursor: "pointer",
                    }}
                  >
                    {PAYMENT_METHOD_ICONS[method]}
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#046287"
                    >
                      {PAYMENT_METHOD_LABELS[method] || formatLabel(method)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Products section */}
      <Box sx={{ px: { xs: 2, md: 4 }, pb: 6 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Products
        </Typography>

        {/* Search + sort toolbar */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 320,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {SORT_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => setSort(opt.value)}
                sx={{
                  cursor: "pointer",
                  bgcolor: sort === opt.value ? "#046287" : undefined,
                  color: sort === opt.value ? "white" : undefined,
                  "&:hover": {
                    bgcolor: sort === opt.value ? "#046287" : undefined,
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {productsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress sx={{ color: "#046287" }} />
          </Box>
        ) : products.length === 0 ? (
          <Typography color="text.secondary">
            {debouncedSearch
              ? `No products matching "${debouncedSearch}".`
              : "No products listed yet."}
          </Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {products.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.id || product._id}>
                  <ProductCard product={product} showShop={false} />
                </Grid>
              ))}
            </Grid>
            <Box
              ref={sentinelRef}
              sx={{ py: 4, display: "flex", justifyContent: "center" }}
            >
              {isFetchingNextPage && (
                <CircularProgress size={28} sx={{ color: "#046287" }} />
              )}
            </Box>
          </>
        )}
      </Box>

      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        entityType="SHOP"
        entityId={resolvedShopId}
        entityName={shop.shopName || "this shop"}
      />

      <UnfollowConfirmDialog
        open={unfollowConfirmOpen}
        onClose={() => setUnfollowConfirmOpen(false)}
        onConfirm={() => {
          toggleFollow(true);
          setUnfollowConfirmOpen(false);
        }}
        entityName={shop.shopName}
        isPending={followPending}
      />

      <Snackbar
        open={!!shareSnackbar}
        autoHideDuration={3000}
        onClose={() => setShareSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setShareSnackbar(null)}
          sx={{ width: "100%" }}
        >
          {shareSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShopPage;
